import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { formatDate, titleCase } from '../../lib/format'
import type { Organization } from '../../types'
import {
  Alert, Badge, Button, Card, EmptyState, Skeleton,
} from '../../components/ui'
import { UsersGroupIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/deleted_organizations.html +
 * its view admin/routes.py:deleted_organizations() (~1364-1367), which reads
 * org_service.list_deleted_orgs() - organisations with status "deleted"
 * (dissolved) or "suspended", newest deleted_at/suspended_at first.
 *
 * GET /api/v1/admin/orgs/deleted and POST /api/v1/admin/orgs/<id>/restore are
 * being added by a teammate alongside this page; org_service already has
 * list_deleted_orgs()/restore_org() backing them. dissolve_org() already
 * writes `dissolved_members: [{uid, name, email, previous_role}, ...]` onto
 * the org record when it's dissolved (org_service.py ~145-183) - a merely
 * *suspended* org never gets that field, so the "Former members" chip list
 * below is naturally empty/absent for those without any extra status check. */

interface DissolvedMember { uid?: string; name?: string; email?: string; previous_role?: string }

interface DeletedOrg extends Organization {
  deleted_at?: string
  suspended_at?: string
  dissolved_members?: DissolvedMember[]
}

interface DeletedOrgsResponse { ok: boolean; items: DeletedOrg[] }

const QUERY_KEY = ['admin', 'orgs-deleted']

export default function AdminDeletedOrganizations() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<DeletedOrgsResponse>('/admin/orgs/deleted?limit=500'),
  })

  const orgs = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Deleted Organisations</h1>
          <p className="page-head__sub">
            Dissolved organisations are hidden from the rest of the platform but their records are
            retained here. Members were reverted to ordinary users. You can restore an organisation
            to make it active again (members are not automatically re-added).
          </p>
        </div>
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={90} /><Skeleton h={90} /></div>
      ) : orgs.length === 0 ? (
        <EmptyState icon={<UsersGroupIcon size={22} />} title="No deleted organisations" body="" />
      ) : (
        <div className="stack">
          {orgs.map((o) => <DeletedOrgCard key={o.id} org={o} />)}
        </div>
      )}
    </>
  )
}

function DeletedOrgCard({ org }: { org: DeletedOrg }) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [expanded, setExpanded] = useState(false)
  const isDissolved = org.status === 'deleted'
  const members = org.dissolved_members ?? []

  const restore = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/admin/orgs/${org.id}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  return (
    <Card pad>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 'var(--sp-2)' }}>
            <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.name || '—'}</strong>
            <Badge tone={isDissolved ? 'danger' : 'warning'}>{isDissolved ? 'Dissolved' : 'Suspended'}</Badge>
          </div>
          <p className="page-head__sub" style={{ margin: '2px 0 0', fontSize: 'var(--text-xs)' }}>
            {titleCase(org.type)} · {formatDate(org.deleted_at || org.suspended_at)}
          </p>
        </div>
        <Button
          size="sm" variant="secondary" loading={restore.isPending} style={{ flexShrink: 0 }}
          onClick={async () => {
            if (await confirm({ title: `Restore '${org.name}'?`, message: 'It will become active again.' })) {
              restore.mutate()
            }
          }}
        >
          Restore
        </Button>
      </div>

      {restore.isError && (
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <Alert tone="danger">{restore.error instanceof ApiError ? restore.error.message : 'That action failed.'}</Alert>
        </div>
      )}

      {members.length > 0 && (
        <div style={{ marginTop: 'var(--sp-3)', paddingTop: 'var(--sp-3)', borderTop: '1px solid var(--border)' }}>
          <Button variant="ghost" size="sm" onClick={() => setExpanded((e) => !e)} style={{ padding: 0, height: 'auto' }}>
            {expanded ? 'Hide' : 'Show'} former members ({members.length})
          </Button>
          {expanded && (
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 'var(--sp-2)' }}>
              {members.map((m, i) => (
                <Badge key={m.uid ?? i} tone="neutral">
                  {m.name}{m.previous_role ? ` · was ${titleCase(m.previous_role)}` : ''}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
